/**
 * Priorities (R8.2, Phase 6, ADR-0002 addendum 2026-07-07, "D-Priority") --
 * the one free-standing checkable entity Compass allows outside an
 * objective's action plan (Non-Goal #1's narrow carve-out). Hard-capped at 5
 * per calendar day (enforced here, at command time); never rolls forward
 * (`listPriorities` reads only `priority.created` events whose `occurredOn`
 * matches the requested date -- there is no carry-forward event in the
 * vocabulary at all, so AC-T8 is structural, not a filter).
 *
 * THE RECONCILIATION RULE (the hardest seam in this phase, per the ADR
 * addendum's own flag): a LINKED Priority's completion is read from
 * `computeActionCompletionStatus` (`objectives/state.ts`) -- the SAME
 * projection every other action consumer reads -- and is NEVER separately
 * written. `completePriority` on a linked Priority never appends
 * `priority.completed`; it routes to the exact same `action.completed`
 * event `completeAction` would produce. A STANDALONE Priority has no action
 * to defer to, so it gets its own `priority.completed` event, idempotent and
 * revertible like `checkin.completed`/`habit.completed`.
 */

import type {
  ClockPort,
  CompassEvent,
  DisplayTimeLabel,
  IdPort,
  IsoDate,
  PriorityActionLink,
  PriorityCompletedEvent,
  PriorityCreatedEvent,
  PriorityId,
  PriorityRemovedEvent,
  PriorityView,
} from '../../contracts';
import { type CommandResult, domainError } from '../event-log/command-error';
import { eventsOfTypes, excludingReverted, revertedEventIds } from '../event-log/event-log';
import { buildObjectiveStates, computeActionCompletionStatus } from '../objectives/state';

type Ports = { readonly clock: ClockPort; readonly id: IdPort };

export const PRIORITY_DAILY_CAP = 5;

interface PriorityState {
  readonly priorityId: PriorityId;
  readonly title: string;
  readonly linkedAction: PriorityActionLink | null;
  readonly timeLabel: DisplayTimeLabel | null;
  readonly occurredOn: string;
  removed: boolean;
}

/** Rebuild every Priority's static (non-completion) state from the log, in canonical order. */
function buildPriorityStates(events: readonly CompassEvent[]): Map<PriorityId, PriorityState> {
  const states = new Map<PriorityId, PriorityState>();
  for (const event of eventsOfTypes(events, ['priority.created', 'priority.removed'])) {
    if (event.type === 'priority.created') {
      states.set(event.priorityId, {
        priorityId: event.priorityId,
        title: event.title,
        linkedAction: event.linkedAction,
        timeLabel: event.timeLabel,
        occurredOn: event.occurredOn,
        removed: false,
      });
    } else {
      const state = states.get(event.priorityId);
      if (state) state.removed = true;
    }
  }
  return states;
}

/**
 * Count of (non-removed) Priorities created for `date` -- the cap
 * denominator. Removed Priorities free their slot (a mis-add correction, not
 * a way to game the cap -- removing one and re-adding another still counts
 * against 5 concurrently-live Priorities, matching the objective cap's own
 * "frees a slot" precedent for archiving).
 */
function countLivePrioritiesForDate(
  events: readonly CompassEvent[],
  date: string,
): number {
  const states = buildPriorityStates(events);
  let count = 0;
  for (const state of states.values()) {
    if (state.occurredOn === date && !state.removed) count += 1;
  }
  return count;
}

/**
 * Today's Priorities list (AC-T8: no cross-day query exists). Ordered by
 * eventId (creation order) via the caller's already-canonically-sorted
 * `events` array -- `eventsOfTypes` preserves that order.
 */
export function listPriorities(
  events: readonly CompassEvent[],
  date: IsoDate | string,
): readonly PriorityView[] {
  const states = buildPriorityStates(events);
  const objectiveStates = buildObjectiveStates(events);
  const actionCompletion = computeActionCompletionStatus(events);
  const standaloneCompletion = resolveStandaloneCompletion(events);

  const views: PriorityView[] = [];
  for (const state of states.values()) {
    if (state.occurredOn !== date || state.removed) continue;

    if (state.linkedAction !== null) {
      const objective = objectiveStates.get(state.linkedAction.objectiveId);
      const completion = actionCompletion.get(state.linkedAction.actionId);
      views.push({
        priorityId: state.priorityId,
        title: state.title,
        linkedAction: {
          objectiveId: state.linkedAction.objectiveId,
          objectiveTitle: objective?.title ?? '',
          objectiveCategory: objective?.category ?? null,
          actionId: state.linkedAction.actionId,
        },
        timeLabel: state.timeLabel,
        completed: completion !== undefined,
        entryEventId: (completion?.entryEventId ?? null) as PriorityView['entryEventId'],
      });
    } else {
      const completion = standaloneCompletion.get(state.priorityId);
      views.push({
        priorityId: state.priorityId,
        title: state.title,
        linkedAction: null,
        timeLabel: state.timeLabel,
        completed: completion !== undefined,
        entryEventId: (completion ?? null) as PriorityView['entryEventId'],
      });
    }
  }
  return views;
}

/**
 * Standalone-Priority completion: the winning (last, non-reverted)
 * `priority.completed` event's ID per `priorityId` -- mirrors
 * `computeActionCompletionStatus`'s "duplicates collapse, revert makes it as
 * if absent" shape.
 */
function resolveStandaloneCompletion(
  events: readonly CompassEvent[],
): ReadonlyMap<PriorityId, string> {
  const active = excludingReverted(eventsOfTypes(events, ['priority.completed', 'log_entry.reverted']));
  const completion = new Map<PriorityId, string>();
  for (const event of active) {
    if (event.type !== 'priority.completed') continue;
    completion.set(event.priorityId, event.eventId);
  }
  return completion;
}

function requireLivePriority(
  events: readonly CompassEvent[],
  priorityId: PriorityId,
): PriorityState {
  const state = buildPriorityStates(events).get(priorityId);
  if (!state || state.removed) {
    throw domainError('priority_not_found', `priority ${priorityId} not found`);
  }
  return state;
}

/**
 * Rejects with `priority_cap_reached` at 5 for `date` (OQ18's hard-cap
 * default). `linkedAction`, when present, must reference an EXISTING action
 * on an existing (non-archived) objective -- never creates one.
 */
export function createPriority(
  events: readonly CompassEvent[],
  date: IsoDate,
  input: {
    readonly title: string;
    readonly linkedAction: PriorityActionLink | null;
    readonly timeLabel: DisplayTimeLabel | null;
  },
  ports: Ports,
): CommandResult<PriorityCreatedEvent> {
  if (input.title.trim().length === 0) {
    throw domainError('invalid_input', 'priority title must not be empty');
  }

  if (countLivePrioritiesForDate(events, date) >= PRIORITY_DAILY_CAP) {
    throw domainError(
      'priority_cap_reached',
      `at most ${PRIORITY_DAILY_CAP} Priorities per day; ${date} already has ${PRIORITY_DAILY_CAP}`,
    );
  }

  if (input.linkedAction !== null) {
    const objective = buildObjectiveStates(events).get(input.linkedAction.objectiveId);
    const action = objective?.actionsById.get(input.linkedAction.actionId);
    if (!objective || objective.status === 'archived' || !action || action.removed) {
      throw domainError(
        'action_not_found',
        `action ${input.linkedAction.actionId} not found on objective ${input.linkedAction.objectiveId}`,
      );
    }
  }

  const event: PriorityCreatedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'priority.created',
    schemaVersion: 1,
    occurredOn: date,
    recordedAt: ports.clock.now(),
    priorityId: ports.id.nextRawId() as PriorityId,
    title: input.title,
    linkedAction: input.linkedAction,
    timeLabel: input.timeLabel,
  };

  return { events: [event] };
}

/**
 * The reconciliation entry point. Returns EITHER a `priority.completed`
 * event (standalone) OR nothing (linked -- the caller is responsible for
 * completing the underlying action via `objectives/commands.ts`'s
 * `completeAction` instead; this function only tells the caller which path
 * applies, per the ADR's "command-layer branch, not a type-level
 * constraint" ruling). Never valid against an already-removed Priority.
 */
export function resolvePriorityCompletionPath(
  events: readonly CompassEvent[],
  priorityId: PriorityId,
): { readonly kind: 'standalone' } | { readonly kind: 'linked'; readonly link: PriorityActionLink } {
  const state = requireLivePriority(events, priorityId);
  return state.linkedAction === null
    ? { kind: 'standalone' }
    : { kind: 'linked', link: state.linkedAction };
}

/**
 * STANDALONE Priorities only (see `resolvePriorityCompletionPath` above --
 * the caller must never invoke this for a linked Priority). Idempotent:
 * completing an already-completed standalone Priority appends another
 * `priority.completed` (duplicates collapse in derivation, first earns XP,
 * same convention as `checkin.completed`/`habit.completed`).
 */
export function completeStandalonePriority(
  events: readonly CompassEvent[],
  priorityId: PriorityId,
  date: IsoDate,
  ports: Ports,
): CommandResult<PriorityCompletedEvent> {
  requireLivePriority(events, priorityId);

  const event: PriorityCompletedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'priority.completed',
    schemaVersion: 1,
    occurredOn: date,
    recordedAt: ports.clock.now(),
    priorityId,
  };

  return { events: [event] };
}

/**
 * Mis-add correction (D-Priority's flagged, minor addition beyond the
 * spec's literal text -- see the module doc comment). Never valid against an
 * already-completed Priority: a completed standalone Priority is undone via
 * `log_entry.reverted` targeting `priority.completed`; a completed linked
 * Priority is undone via `log_entry.reverted` targeting the underlying
 * `action.completed` -- either way, "remove" and "undo a completion" stay
 * distinct operations, mirroring how a completed action is never
 * simultaneously "removable" in `objectives/commands.ts`.
 */
export function removePriority(
  events: readonly CompassEvent[],
  priorityId: PriorityId,
  ports: Ports,
): CommandResult<PriorityRemovedEvent> {
  const state = requireLivePriority(events, priorityId);

  const isCompleted =
    state.linkedAction !== null
      ? computeActionCompletionStatus(events).has(state.linkedAction.actionId)
      : resolveStandaloneCompletion(events).has(priorityId);
  if (isCompleted) {
    throw domainError(
      'invalid_input',
      `priority ${priorityId} is already completed; revert the completion instead of removing`,
    );
  }

  const event: PriorityRemovedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'priority.removed',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    priorityId,
  };

  return { events: [event] };
}

// Re-exported so create-domain-api.ts/realDomain.ts can defensively confirm
// a revert target's reverted-set membership without importing event-log
// internals directly at the call site (kept minimal; not otherwise used
// inside this module beyond resolveStandaloneCompletion above).
export { revertedEventIds };
